import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { CellSpacing } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import { Tooltip } from '@cbhq/cds-web/overlays/Tooltip/Tooltip';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Table } from '@cbhq/cds-web/tables/Table';
import { TableBody } from '@cbhq/cds-web/tables/TableBody';
import { TableCell } from '@cbhq/cds-web/tables/TableCell';
import { TableFooter } from '@cbhq/cds-web/tables/TableFooter';
import { TableHeader } from '@cbhq/cds-web/tables/TableHeader';
import { TableRow } from '@cbhq/cds-web/tables/TableRow';
import { TextBody, TextHeadline, TextLabel2, TextTitle1 } from '@cbhq/cds-web/typography';
import { abbreviateHash } from '@cbhq/commerce-shared/utils/helper';

import { AnalyticEvent } from ':fat/analytics/events';
import { logGenericEvent, logRender } from ':fat/analytics/logActions';
import { logFundsAccessCriticalStep, logFundsAccessError } from ':fat/analytics/logMetrics';
import { FundSettlementFAQ } from ':fat/components/FAQ/FundsSettlementFAQ';
import { TwoColumnLayout } from ':fat/components/PageLayout/TwoColumnLayout';
import { ALL_NETWORKS, CURRENCY_DATA, Network } from ':fat/core/networks';
import { formatCrypto, formatFiat } from ':fat/helpers/formatting';
import { getCurrencyIcon } from ':fat/helpers/getCurrencyIcon';
import { reportError } from ':fat/init/bugsnag';
import { signTransactions } from ':fat/messaging/entities/securityFrame/actions';
import { messages } from ':fat/pages/Review/ReviewPageMessages';
import { AppRoutes } from ':fat/routing/routes';
import { useTransactions } from ':fat/TransactionContext';

const listCellSpacing: CellSpacing = { spacingVertical: 0, spacingHorizontal: 1 };

export function ReviewPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { fingerprint } = useParams();
  const toast = useToast();

  const {
    transactions,
    setTransactions,
    networksWithTransactions,
    anyTransactions,
    destinationAddresses,
    cryptoInputAmounts,
    cryptoNetworkFeeAmounts,
    cryptoCoinbaseFeeAmounts,
    fiatInputAmounts,
    fiatNetworkFeeAmounts,
    fiatCoinbaseFeeAmounts,
    totalNetworkFee,
    totalCoinbaseFee,
    totalProceeds,
  } = useTransactions();

  useEffect(() => {
    if (!anyTransactions) {
      logFundsAccessError('show_error_toast');
      toast.show(formatMessage(messages.noTransactionsErrorToast));
      navigate(AppRoutes.SECURITY_FRAME_LOADER);
    }
  }, [anyTransactions, navigate, toast, formatMessage]);

  useEffect(() => {
    logRender(AnalyticEvent.ReviewPageRendered);
  }, []);

  const handleBackPress = useCallback(() => {
    navigate(AppRoutes.ENTER_WALLET_ADDRESSES.replace(':fingerprint', fingerprint ?? ''), {
      replace: true,
    });
  }, [navigate, fingerprint]);

  const handleWithdrawPress = useCallback(() => {
    logGenericEvent(AnalyticEvent.ReviewWithdrawalClicked);

    async function signAndContinue() {
      try {
        logFundsAccessCriticalStep({ step: 'started_withdrawal' });
        const signedTxs = await signTransactions(transactions);
        setTransactions(signedTxs);
        Object.keys(signedTxs).forEach((network) => {
          logGenericEvent(AnalyticEvent.WithdrawalSigningSuccess, {
            transactionCount: signedTxs[network as Network].length,
            fingerprint,
            network,
          });
        });
        navigate(AppRoutes.BROADCASTING.replace(':fingerprint', fingerprint ?? ''));
      } catch (message) {
        logFundsAccessError('show_error_toast');
        if ((message as MainPageMessage)?.kind === 'NO_SIGNER') {
          toast.show(formatMessage(messages.noSeedPhraseErrorToast));
          navigate(AppRoutes.SECURITY_FRAME_LOADER);
        } else if ((message as MainPageMessage)?.kind === 'SIGNING_ERROR') {
          // eslint-disable-next-line no-console
          console.error((message as { error: string }).error);
          reportError((message as { error: string }).error, {
            context: 'transactionSigning',
            metadata: [
              {
                name: 'details',
                data: {
                  transactionCount: (Object.keys(transactions) as Network[]).reduce((obj, key) => {
                    const nextObj = obj;
                    nextObj[key] = transactions[key].length;
                    return obj;
                  }, {} as Record<Network, number>),
                  error: (message as { error: string }).error,
                },
              },
            ],
          });
          toast.show(formatMessage(messages.problemSigningErrorToast));
          navigate(AppRoutes.SECURITY_FRAME_LOADER);
        }
      }
    }

    if (anyTransactions) {
      void signAndContinue();
    }
  }, [anyTransactions, fingerprint, setTransactions, transactions, toast, formatMessage, navigate]);

  return (
    <TwoColumnLayout
      primaryContent={
        <VStack gap={2} width="100%">
          <VStack gap={2}>
            <TextTitle1 as="h1">{formatMessage(messages.title)}</TextTitle1>
            <TextBody color="foregroundMuted" as="p">
              {formatMessage(messages.description)}
            </TextBody>
          </VStack>

          <VStack spacingVertical={4} gap={3}>
            <Table bordered variant="ruled">
              <TableHeader>
                <TableRow>
                  <TableCell title={formatMessage(messages.asset)} width="40%" />
                  <TableCell
                    title={formatMessage(messages.coinbaseFee)}
                    width="20%"
                    alignItems="flex-end"
                  />
                  <TableCell
                    title={formatMessage(messages.networkFee)}
                    width="20%"
                    alignItems="flex-end"
                  />
                  <TableCell
                    title={formatMessage(messages.balance)}
                    width="20%"
                    alignItems="flex-end"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {ALL_NETWORKS.map((network) =>
                  !networksWithTransactions.includes(network) ? null : (
                    <TableRow key={`row--${network}`}>
                      <TableCell>
                        <ListCell
                          compact
                          title={CURRENCY_DATA[network].name}
                          description={
                            <Tooltip content={destinationAddresses[network]}>
                              <TextBody as="p" color="foregroundMuted">
                                {abbreviateHash(destinationAddresses[network], 6, 6)}
                              </TextBody>
                            </Tooltip>
                          }
                          media={
                            <Avatar
                              alt={CURRENCY_DATA[network].name}
                              src={getCurrencyIcon(
                                CURRENCY_DATA[network].symbol.toLowerCase() as Network,
                              )}
                              size="xl"
                            />
                          }
                          outerSpacing={listCellSpacing}
                        />
                      </TableCell>

                      <TableCell alignItems="flex-end">
                        <VStack>
                          <TextBody as="p" align="end">
                            {formatFiat(fiatCoinbaseFeeAmounts[network])}
                          </TextBody>
                          <TextLabel2 as="p" color="foregroundMuted" align="end">
                            {formatCrypto(network, cryptoCoinbaseFeeAmounts[network] ?? 0n)}
                          </TextLabel2>
                        </VStack>
                      </TableCell>
                      <TableCell alignItems="flex-end">
                        <VStack>
                          <TextBody as="p" align="end">
                            {formatFiat(fiatNetworkFeeAmounts[network])}
                          </TextBody>
                          <TextLabel2 as="p" color="foregroundMuted" align="end">
                            {formatCrypto(network, cryptoNetworkFeeAmounts[network] ?? 0n)}
                          </TextLabel2>
                        </VStack>
                      </TableCell>

                      <TableCell alignItems="flex-end">
                        <VStack>
                          <TextBody as="p" align="end">
                            {formatFiat(fiatInputAmounts[network])}
                          </TextBody>
                          <TextLabel2 as="p" color="foregroundMuted" align="end">
                            {formatCrypto(network, cryptoInputAmounts[network] ?? 0)}
                          </TextLabel2>
                        </VStack>
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
              <TableFooter>
                <TableRow fullWidth>
                  <VStack width="100%" gap={3}>
                    <HStack justifyContent="space-between">
                      <HStack alignItems="center">
                        <TextBody as="p" color="foregroundMuted">
                          {formatMessage(messages.totalCoinbaseFee)}
                        </TextBody>
                      </HStack>
                      <VStack>
                        <TextHeadline as="p">{formatFiat(totalCoinbaseFee)}</TextHeadline>
                      </VStack>
                    </HStack>

                    <HStack justifyContent="space-between">
                      <HStack alignItems="center">
                        <TextBody as="p" color="foregroundMuted">
                          {formatMessage(messages.totalNetworkFee)}
                        </TextBody>
                        <Box spacingStart={1}>
                          <Tooltip content={formatMessage(messages.totalNetworkFeeTooltip)}>
                            <Icon size="xs" name="info" color="foreground" />
                          </Tooltip>
                        </Box>
                      </HStack>
                      <VStack>
                        <TextHeadline as="p">{formatFiat(totalNetworkFee)}</TextHeadline>
                      </VStack>
                    </HStack>

                    <HStack justifyContent="space-between">
                      <HStack alignItems="center">
                        <TextHeadline as="p">{formatMessage(messages.totalProceeds)}</TextHeadline>
                        <Box spacingStart={1}>
                          <Tooltip content={formatMessage(messages.totalProceedsTooltip)}>
                            <Icon size="xs" name="info" color="foreground" />
                          </Tooltip>
                        </Box>
                      </HStack>
                      <VStack>
                        <TextHeadline as="p">{formatFiat(totalProceeds)}</TextHeadline>
                      </VStack>
                    </HStack>
                  </VStack>
                </TableRow>
              </TableFooter>
            </Table>
          </VStack>
          <HStack gap={1} justifyContent="flex-end">
            <Button variant="secondary" onPress={handleBackPress}>
              {formatMessage(messages.back)}
            </Button>
            <Button variant="primary" onPress={handleWithdrawPress} disabled={!anyTransactions}>
              {formatMessage(messages.withdraw)}
            </Button>
          </HStack>
        </VStack>
      }
      secondaryContent={<FundSettlementFAQ />}
    />
  );
}
